import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Alert, Avatar, Button, Col, Empty, Image, InputNumber, Popconfirm, Result, Row, Select, Space, Spin, Tag, Tooltip } from 'antd'
import {
    ClockCircleOutlined,
    DeleteOutlined,
    GiftOutlined,
    InfoCircleOutlined,
    MinusOutlined,
    PlusOutlined,
    ShoppingCartOutlined,
    ShoppingOutlined,
    TagOutlined,
    UserOutlined,
} from '@ant-design/icons'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { SectionBadge } from '../../../components/SectionCard'
import { getPrice } from '../../../helpers/price'
import { openApi } from '../../../helpers/request'
import controller from './cart.controller'
import { ActivityDot, LiveSwitch } from './Carts'
import { CartProductPicker } from './CartProductPicker'
import { deliveryLabel, errorMessage, formatDateTime, formatPhone, timeAgo, toDisplay, userName } from './carts.helpers'
import './carts.less'

const MetaRow = ({ icon, label, children }: { icon: React.ReactNode, label: string, children: React.ReactNode }) => (
    <div className="cart-meta">
        <span className="cart-meta__label">{icon}{label}</span>
        <span className="cart-meta__value">{children}</span>
    </div>
)

/** Почему пользователь не сможет заказать позицию — по одной метке на причину */
const ItemWarnings = ({ item }: { item: any }) => (
    <>
        {item.productDeleted && <Tag color="red" className="carts-tag">{i.t('carts:detail.tags.deleted')}</Tag>}
        {!item.productDeleted && item.available === false && <Tag color="red" className="carts-tag">{i.t('carts:detail.tags.hidden')}</Tag>}
        {!item.productDeleted && item.available !== false && item.stock !== null && item.count > item.stock && (
            <Tag color="orange" className="carts-tag">{i.t('carts:detail.tags.outOfStock', { stock: toDisplay(item.stock, item.step) })}</Tag>
        )}
        {!item.allowAddingToCart && <Tag color="orange" className="carts-tag">{i.t('carts:detail.tags.notForCart')}</Tag>}
    </>
)

const ItemDescription = ({ item }: { item: any }) => {
    // Модификаторы группируем, как в карточке заказа
    const groups: Record<string, any[]> = {}
    item.modifiers.forEach((modifier: any) => {
        const key = modifier.group ?? ''
        groups[key] = [...(groups[key] ?? []), modifier]
    })

    return (
        <div className="cart-item__meta">
            <div className="cart-item__name">
                {item.name ?? `#${item.productId}`}
                {item.productDeleted
                    ? null
                    : <a className="cart-item__link" href={`/shop/catalog/product/${item.productId}`} target="_blank" rel="noreferrer">#{item.productId}</a>
                }
            </div>
            {item.vendorCode && <div className="carts-muted">{i.t('carts:detail.vendorCode')}: {item.vendorCode}</div>}
            {(item.params.length > 0 || item.label) && (
                <div className="cart-item__tags">
                    {item.params.map((param: any, index: number) => (
                        <Tag key={index} className="carts-tag">{param.category}: {param.value}</Tag>
                    ))}
                    {item.label && <Tag className="carts-tag">{item.label}</Tag>}
                </div>
            )}
            {Object.keys(groups).map((group: string) => (
                <div key={group} className="cart-item__modifiers">
                    {group && <span className="cart-item__modifiers-group">{group}:</span>}
                    {groups[group].map((modifier: any, index: number) => (
                        <span key={index} className="cart-item__modifier">
                            {modifier.name ?? '—'} × {modifier.count}
                            {modifier.price > 0 && <span className="carts-muted"> (+{getPrice(modifier.price)})</span>}
                        </span>
                    ))}
                </div>
            ))}
            <div className="cart-item__tags"><ItemWarnings item={item} /></div>
        </div>
    )
}

const ItemPrice = ({ item }: { item: any }) => (
    <div className="cart-item__price">
        {item.oldPrice > item.price && <div className="cart-item__price-old">{getPrice(item.oldPrice * item.count)}</div>}
        <div className="cart-item__price-total">{getPrice(item.total)}</div>
        <div className="carts-muted">
            {/* Цена — за шаг товара: у весового «120 ₽ за 100 г × 2», а не «120 ₽ × 200 г» */}
            {item.step > 1
                ? i.t('carts:detail.pricePerStep', { price: getPrice(item.price), step: item.step, unit: item.unit ?? '', count: item.count })
                : `${getPrice(item.price)} × ${item.count}`
            }
        </div>
    </div>
)

const QuantityEditor = observer(({ item }: { item: any }) => {
    const step = item.step || 1
    const current = toDisplay(item.count, step)
    const draft = controller.drafts[item.id]
    const value = draft !== undefined ? draft : current

    const save = (next: number | null) => controller.saveQuantity(item, next)

    return (
        <div className="cart-item__quantity">
            <Space.Compact>
                <Button
                    icon={<MinusOutlined />}
                    disabled={controller.busy || current - step <= 0}
                    onClick={() => save(current - step)}
                />
                <InputNumber
                    min={step}
                    step={step}
                    value={value}
                    disabled={controller.busy}
                    style={{ width: 64 }}
                    controls={false}
                    onChange={(next) => controller.setDraft(item.id, next)}
                    onBlur={() => draft !== undefined && save(draft)}
                    onPressEnter={() => draft !== undefined && save(draft)}
                />
                <Button
                    icon={<PlusOutlined />}
                    disabled={controller.busy}
                    onClick={() => save(current + step)}
                />
            </Space.Compact>
            {item.unit && <span className="carts-muted">{item.unit}</span>}
        </div>
    )
})

const CartItem = observer(({ item, editable }: { item: any, editable: boolean }) => {
    const unavailable = item.productDeleted || item.available === false

    return (
        <div className={`cart-item${controller.flashIds.includes(item.id) ? ' cart-item--flash' : ''}${unavailable ? ' cart-item--unavailable' : ''}`}>
            <div className="cart-item__image">
                {item.image
                    ? <Image src={item.image} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 10 }} />
                    : <Avatar shape="square" size={56} icon={item.free ? <GiftOutlined /> : <ShoppingOutlined />} className="carts-thumb" />
                }
            </div>

            <ItemDescription item={item} />

            {editable
                ? <QuantityEditor item={item} />
                : <div className="cart-item__quantity">{toDisplay(item.count, item.step)} {item.unit ?? ''}</div>
            }

            <ItemPrice item={item} />

            <div className="cart-item__remove">
                {editable && (
                    <Popconfirm
                        title={i.t('carts:detail.removeConfirm', { name: item.name ?? `#${item.productId}` })}
                        okText={i.t('carts:detail.remove')}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => controller.removeItem(item)}
                    >
                        <Tooltip title={i.t('carts:detail.remove')}>
                            <Button danger type="text" icon={<DeleteOutlined />} disabled={controller.busy} />
                        </Tooltip>
                    </Popconfirm>
                )}
            </div>
        </div>
    )
})

const UserBlock = ({ cart }: { cart: any }) => {
    const user = cart.user

    return (
        <div className="order__block cart-block">
            <SectionBadge
                icon={<UserOutlined />}
                text={i.t('carts:fields.user')}
                background="linear-gradient(135deg, #4f8ff7, #7fb2ff)"
            />
            {!user
                ? <span className="carts-muted">{i.t('carts:user.missing')}</span>
                : <>
                    <div className="cart-user">
                        <Avatar size={44} icon={<UserOutlined />} className={user.registered ? 'carts-user__avatar' : 'carts-user__avatar carts-user__avatar--guest'} />
                        <div>
                            <div className="cart-user__name">{userName(user)}</div>
                            <Space size={4} wrap>
                                <Tag className="carts-tag" color={user.registered ? 'green' : undefined}>
                                    {user.registered ? i.t('carts:filters.registered') : i.t('carts:user.guest')}
                                </Tag>
                                {user.deleted && <Tag color="red" className="carts-tag">{i.t('carts:user.deleted')}</Tag>}
                            </Space>
                        </div>
                    </div>
                    {user.phone && <MetaRow icon={null} label={i.t('carts:user.phone')}><a href={`tel:+${user.phone.replace(/\D+/g, '')}`}>{formatPhone(user.phone)}</a></MetaRow>}
                    {user.email && <MetaRow icon={null} label={i.t('carts:user.email')}><a href={`mailto:${user.email}`}>{user.email}</a></MetaRow>}
                    <MetaRow icon={null} label={i.t('carts:user.orders')}>
                        {user.ordersCount > 0 ? user.ordersCount : <span className="carts-muted">0</span>}
                    </MetaRow>
                    {user.lastOrder && (
                        <MetaRow icon={null} label={i.t('carts:user.lastOrder')}>
                            <a href={`/shop/orders/${user.lastOrder.id}`} target="_blank" rel="noreferrer">#{user.lastOrder.id}</a>
                            <span className="carts-muted"> · {getPrice(user.lastOrder.sum)} · {formatDateTime(user.lastOrder.createdAt)}</span>
                        </MetaRow>
                    )}
                    <Button block href={`/users/${user.id}`}>{i.t('carts:user.open')}</Button>
                </>
            }
        </div>
    )
}

const SummaryBlock = observer(({ cart }: { cart: any }) => {
    const totals = cart.totals
    const savings = totals.oldSum - totals.sum

    return (
        <div className="order__block cart-block">
            <SectionBadge
                icon={<ShoppingCartOutlined />}
                text={i.t('carts:detail.summary')}
                background="linear-gradient(135deg, var(--primary-color), #9186e0)"
            />
            <div className="cart-summary">
                <div className="cart-summary__sum">{getPrice(totals.sum)}</div>
                <div className="carts-muted">
                    {i.t('carts:detail.positions', { n: totals.positions })} · {totals.quantity} {i.t('carts:units.pcs')}
                </div>
                {savings > 0.009 && (
                    <div className="cart-summary__savings">
                        {i.t('carts:detail.savings')}: <b>{getPrice(savings)}</b>
                        <span className="carts-muted"> ({i.t('carts:detail.oldSum')} {getPrice(totals.oldSum)})</span>
                    </div>
                )}
            </div>

            {cart.warehousesEnabled && (
                <div className="cart-summary__warehouse">
                    <div className="cart-meta__label">{i.t('carts:detail.warehouse')}</div>
                    <Select
                        allowClear
                        style={{ width: '100%' }}
                        placeholder={i.t('carts:detail.selectWarehouse')}
                        value={cart.warehouse?.id ?? undefined}
                        onChange={(value) => controller.setWarehouse(value ?? null)}
                        options={cart.warehouses.map((warehouse: any) => ({ value: warehouse.id, label: warehouse.name ?? `#${warehouse.id}` }))}
                    />
                    {!cart.warehouse && <div className="carts-muted cart-summary__note">{i.t('carts:detail.noWarehouse')}</div>}
                </div>
            )}

            <Alert type="info" showIcon icon={<InfoCircleOutlined />} message={i.t('carts:detail.sumHint')} className="cart-summary__hint" />
        </div>
    )
})

const PromoBlock = observer(({ cart }: { cart: any }) => {
    const promo = cart.promoCode

    return (
        <div className="order__block cart-block">
            <SectionBadge
                icon={<TagOutlined />}
                text={i.t('carts:detail.promo')}
                background="linear-gradient(135deg, #b37feb, #d3adf7)"
            />
            {!promo
                ? <span className="carts-muted">{i.t('carts:detail.noPromo')}</span>
                : <>
                    {promo.code
                        ? <Space size={8} wrap>
                            <Tag icon={<GiftOutlined />} color="purple" className="cart-promo__code">{promo.code}</Tag>
                            {promo.name && <span>{promo.name}</span>}
                            <a href={`/shop/coupons/${promo.id}`} target="_blank" rel="noreferrer">{i.t('carts:detail.openCoupon')}</a>
                        </Space>
                        : <span>
                            {i.t('carts:detail.invited')}{' '}
                            {promo.invitedUserId && <a href={`/users/${promo.invitedUserId}`} target="_blank" rel="noreferrer">#{promo.invitedUserId}</a>}
                        </span>
                    }
                    <Popconfirm
                        title={i.t('carts:detail.removePromoConfirm')}
                        okText={i.t('carts:detail.removePromo')}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => controller.removePromoCode()}
                    >
                        <Button danger block disabled={controller.busy}>{i.t('carts:detail.removePromo')}</Button>
                    </Popconfirm>
                </>
            }
        </div>
    )
})

const InfoBlock = ({ cart }: { cart: any }) => (
    <div className="order__block cart-block">
        <SectionBadge
            icon={<ClockCircleOutlined />}
            text={i.t('carts:detail.info')}
            background="linear-gradient(135deg, #36cfc9, #87e8de)"
        />
        <MetaRow icon={null} label={i.t('carts:fields.activity')}>
            <Tooltip title={formatDateTime(cart.activityAt)}>
                <Space size={8}><ActivityDot iso={cart.activityAt} />{timeAgo(cart.activityAt)}</Space>
            </Tooltip>
        </MetaRow>
        <MetaRow icon={null} label={i.t('carts:fields.created')}>{formatDateTime(cart.createdAt)}</MetaRow>
        <MetaRow icon={null} label={i.t('carts:fields.delivery')}>
            {cart.delivery ? deliveryLabel(cart.delivery) : <span className="carts-muted">{deliveryLabel(null)}</span>}
        </MetaRow>
    </div>
)

export const CartDetail = observer(() => {
    // @ts-ignore
    const { id } = useParams()
    const [pickerOpen, setPickerOpen] = useState(false)

    useEffect(() => {
        controller.open(Number(id))
        controller.startLive()

        const onVisible = () => {
            if (!document.hidden && controller.live && !controller.busy) controller.load(true)
        }
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            controller.stopLive()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [id])

    const cart = controller.cart

    const breadcrumb = [
        { title: i.t('carts:title'), href: '/carts' },
        { title: `${i.t('carts:cart')} #${id}` },
    ]

    if (controller.notFound) {
        return (
            <>
                <Titles title={`${i.t('carts:cart')} #${id}`} breadcrumb={breadcrumb} />
                <Result
                    status="404"
                    title={i.t('carts:errors.notFound')}
                    subTitle={i.t('carts:errors.notFoundHint')}
                    extra={<Button type="primary" href="/carts">{i.t('carts:backToList')}</Button>}
                />
            </>
        )
    }

    if (!cart || cart.id !== Number(id)) {
        return (
            <>
                <Titles title={`${i.t('carts:cart')} #${id}`} breadcrumb={breadcrumb} />
                <div className="cart-loading"><Spin /></div>
            </>
        )
    }

    const buttons = [
        { title: i.t('carts:backToList'), href: '/carts', type: 'dashed' },
        ...(cart.items.length > 0 || cart.gifts.length > 0 || cart.promoCode
            ? [{
                title: i.t('carts:detail.clear'),
                type: 'dashed',
                danger: true,
                click: () => {
                    if (window.confirm(i.t('carts:detail.clearConfirm'))) controller.clear()
                },
            }]
            : []),
    ]

    return (
        <>
            <Titles
                title={`${i.t('carts:cart')} #${cart.id}${cart.user ? ` · ${userName(cart.user)}` : ''}`}
                breadcrumb={breadcrumb}
                buttons={buttons}
            />

            <LiveSwitch
                live={controller.live}
                updatedAt={controller.updatedAt}
                onChange={(value) => controller.setLive(value)}
                onRefresh={() => controller.load()}
            />

            {cart.totals.unavailable > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    className="cart-alert"
                    message={i.t('carts:detail.unavailable', { n: cart.totals.unavailable })}
                    description={i.t('carts:detail.unavailableHint')}
                />
            )}

            <Row gutter={[20, 20]}>
                <Col span={24} xl={16}>
                    <div className="order__block cart-block">
                        <SectionBadge
                            icon={<ShoppingCartOutlined />}
                            text={i.t('carts:detail.products')}
                            background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                            toggle={
                                <Button icon={<PlusOutlined />} onClick={() => setPickerOpen(true)} disabled={controller.busy}>
                                    {i.t('carts:detail.add')}
                                </Button>
                            }
                        />

                        {cart.items.length === 0
                            ? <Empty className="cart-empty" description={i.t('carts:detail.empty')}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>{i.t('carts:detail.add')}</Button>
                            </Empty>
                            : <div className="cart-items">
                                {cart.items.map((item: any) => <CartItem key={item.id} item={item} editable={!item.productDeleted} />)}
                            </div>
                        }
                    </div>

                    {cart.gifts.length > 0 && (
                        <div className="order__block cart-block cart-block--gifts">
                            <SectionBadge
                                icon={<GiftOutlined />}
                                text={i.t('carts:detail.gifts')}
                                background="linear-gradient(135deg, #52c41a, #95de64)"
                            />
                            <div className="carts-muted">{i.t('carts:detail.giftsHint')}</div>
                            <div className="cart-items">
                                {cart.gifts.map((item: any) => <CartItem key={item.id} item={item} editable={false} />)}
                            </div>
                        </div>
                    )}
                </Col>

                <Col span={24} xl={8}>
                    <div className="cart-side">
                        <SummaryBlock cart={cart} />
                        <UserBlock cart={cart} />
                        <PromoBlock cart={cart} />
                        <InfoBlock cart={cart} />
                    </div>
                </Col>
            </Row>

            <CartProductPicker
                open={pickerOpen}
                warehouseId={cart.warehouse?.id ?? null}
                onClose={() => setPickerOpen(false)}
                onAdd={(product, quantity, modifiers) => controller.addItem(product, quantity, modifiers)}
            />
        </>
    )
})

/** Переход из карточки пользователя: находим (или заводим) его корзину и открываем её */
export const CartByUser = () => {
    // @ts-ignore
    const { userId } = useParams()
    const history = useHistory()
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        openApi('post', `/carts/user/${userId}`)
            .then(result => history.replace(`/carts/${result.data.id}`))
            .catch(err => setError(errorMessage(err)))
    }, [userId, history])

    return (
        <>
            <Titles title={i.t('carts:cart')} breadcrumb={[{ title: i.t('carts:title'), href: '/carts' }]} />
            {error
                ? <Result status="warning" title={error} extra={<Button href={`/users/${userId}`}>{i.t('carts:user.open')}</Button>} />
                : <div className="cart-loading"><Spin tip={i.t('carts:byUser')}><div /></Spin></div>
            }
        </>
    )
}
